import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as aiApi from '@/api/ai.api';

/**
 * Query keys for AI
 */
export const aiKeys = {
  all: ['ai'] as const,
  status: () => [...aiKeys.all, 'status'] as const,
  costs: (params?: { startDate?: string; endDate?: string }) => [...aiKeys.all, 'costs', params] as const,
  costDashboard: () => [...aiKeys.all, 'costs', 'dashboard'] as const,
  proxyStatus: () => [...aiKeys.all, 'proxy', 'status'] as const,
};

/**
 * Fetch AI status
 */
export function useAIStatus() {
  return useQuery({
    queryKey: aiKeys.status(),
    queryFn: () => aiApi.getAIStatus(),
    refetchInterval: 60000, // Refresh every minute
    retry: false, // Don't retry on failure
  });
}

/**
 * Fetch AI costs
 */
export function useAICosts(params?: { startDate?: string; endDate?: string; provider?: string }) {
  return useQuery({
    queryKey: aiKeys.costs(params),
    queryFn: () => aiApi.getAICosts(params),
  });
}

/**
 * Fetch proxy status
 */
export function useProxyStatus() {
  return useQuery({
    queryKey: aiKeys.proxyStatus(),
    queryFn: () => aiApi.getProxyStatus(),
    refetchInterval: 30000, // Refresh every 30 seconds
    retry: false,
  });
}

/**
 * Test proxy connection
 */
export function useTestProxy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => aiApi.testProxy(),
    onSuccess: (data) => {
      queryClient.setQueryData(aiKeys.proxyStatus(), data);
    },
  });
}

/**
 * Fetch AI cost dashboard data
 */
export function useAICostDashboard() {
  return useQuery({
    queryKey: aiKeys.costDashboard(),
    queryFn: () => aiApi.getAICostDashboard(),
    refetchInterval: 60000, // Refresh every minute
  });
}

/**
 * Update AI budget
 */
export function useUpdateAIBudget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { monthlyBudget?: number; usedAmount?: number }) =>
      aiApi.updateAIBudget(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiKeys.costDashboard() });
      queryClient.invalidateQueries({ queryKey: aiKeys.costs() });
    },
  });
}

/**
 * Sync from Anthropic portal
 */
export function useSyncFromAnthropic() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => aiApi.syncFromAnthropic(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiKeys.costDashboard() });
      queryClient.invalidateQueries({ queryKey: aiKeys.costs() });
    },
  });
}

/**
 * Reset to calculated usage
 */
export function useResetToCalculatedUsage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => aiApi.resetToCalculatedUsage(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiKeys.costDashboard() });
      queryClient.invalidateQueries({ queryKey: aiKeys.costs() });
    },
  });
}
